import * as React from 'react'
import { CF_ASSET_MIME, acceptedDrop, cfAssetDragAccepted, dragOverAccepted } from './asset'
import type { AssetMediaType, CfAssetDrag } from './asset'
import { getCurrentAssetDrag, subscribeAssetDrag } from './current-drag'

export function useAssetDrop({
  accept,
  onDrop,
}: {
  accept: readonly AssetMediaType[]
  onDrop: (asset: CfAssetDrag) => void
}) {
  const acceptRef = React.useRef(accept)
  acceptRef.current = accept
  const onDropRef = React.useRef(onDrop)
  onDropRef.current = onDrop
  const [hover, setHover] = React.useState(false)
  const current = React.useSyncExternalStore(subscribeAssetDrag, getCurrentAssetDrag, () => null)

  React.useEffect(() => {
    if (!current) {
      setHover(false)
    }
  }, [current])

  function onDragOver(event: React.DragEvent) {
    // Safari's DataTransfer.types is a DOMStringList and has no includes().
    const types = Array.from(event.dataTransfer.types)
    if (types.includes('Files') || !types.includes(CF_ASSET_MIME)) {
      setHover(false)
      return
    }
    // Own the drop so text/uri-list does not navigate, including a filtered media type.
    event.preventDefault()
    const accepted = dragOverAccepted({
      types,
      asset: getCurrentAssetDrag(),
      accept: acceptRef.current,
    })
    if (!accepted) {
      setHover(false)
      return
    }
    event.dataTransfer.dropEffect = 'copy'
    setHover(true)
  }

  function onDragLeave(event: React.DragEvent) {
    const next = event.relatedTarget
    if (next instanceof Node && event.currentTarget.contains(next)) {
      return
    }
    setHover(false)
  }

  function handleDrop(event: React.DragEvent) {
    setHover(false)
    const types = Array.from(event.dataTransfer.types)
    if (!types.includes(CF_ASSET_MIME)) {
      return
    }
    if (types.includes('Files')) {
      return
    }
    event.preventDefault()
    const dropped = acceptedDrop({
      types,
      assetRaw: event.dataTransfer.getData(CF_ASSET_MIME),
      accept: acceptRef.current,
    })
    if (!dropped) {
      return
    }
    onDropRef.current(dropped)
  }

  const asset = hover && current && cfAssetDragAccepted(current, accept) ? current : null

  return {
    hover,
    isOver: hover,
    active: current !== null && cfAssetDragAccepted(current, accept),
    asset,
    onDragEnter: onDragOver,
    onDragOver,
    onDragLeave,
    onDrop: handleDrop,
  }
}
